/**
 * PRD.227 — Invoice-scoped adjustment history.
 *
 * Sits inside the invoice detail (InvoicesTab) below the payment
 * history. Shows only adjustments where `invoice_id = this invoice`,
 * with a "+ Add Adjustment" button that opens the same dialog used on
 * the patient Billing Across Visits page — pre-linked to this invoice.
 *
 * Patient-level adjustments (no invoice link) do NOT appear here on
 * purpose; users see those in the patient-wide Billing view.
 */

import { useMemo } from 'react';
import { Button } from '@helios/ui/button';
import { usePowerSyncQuery } from '../../adapters/powersync';
import { qBillingAdjustmentsByInvoice, type BillingAdjustmentRow } from '@helios/data-access';
import { phpFromCents, sumMoney, subtractMoney } from '@helios/domain';
import { useFormatMoney } from '../../hooks/useFormatMoney';

interface Props {
  invoiceId: string;
  onAdd?: () => void;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-PH', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

export function InvoiceAdjustmentsList({ invoiceId, onAdd }: Props) {
  const { data: rows = [] } = usePowerSyncQuery<BillingAdjustmentRow>(
    qBillingAdjustmentsByInvoice(invoiceId),
  );
  const fmt = useFormatMoney();

  const totals = useMemo(() => {
    const debits = rows
      .filter((r) => r.adjustment_type === 'debit')
      .map((r) => phpFromCents(r.amount_cents ?? 0));
    const credits = rows
      .filter((r) => r.adjustment_type === 'credit')
      .map((r) => phpFromCents(r.amount_cents ?? 0));
    const debitTotal = sumMoney(debits);
    const creditTotal = sumMoney(credits);
    const net = subtractMoney(debitTotal, creditTotal);
    return { debitTotal, creditTotal, net };
  }, [rows]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-4) var(--sp-5)',
        borderTop: '1px solid var(--border)',
      }}
    >
      {/* PRD.229 §6.3 — Section header + Add Adjustment action. Bottom
          border matches the invoice header divider so each section reads
          as a distinct surface inside invoice detail. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 'var(--sp-3)',
          paddingBottom: 'var(--sp-3)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--sp-3)' }}>
          {/* PRD.229 §6.3 — Match the invoice header's prominence so the
              section stands out as its own surface inside invoice detail. */}
          <h3
            style={{
              margin: 0,
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Adjustments
          </h3>
          <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            Credit / debit corrections linked to this invoice
          </span>
        </div>
        {onAdd && (
          <Button variant="gradient" size="md" onClick={onAdd}>
            + Add Adjustment
          </Button>
        )}
      </div>

      {/* Totals strip */}
      {rows.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-4)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
            flexWrap: 'wrap',
          }}
        >
          <span>
            Debits{' '}
            <strong style={{ color: 'var(--color-warning-text)' }}>{fmt(totals.debitTotal)}</strong>
          </span>
          <span>
            Credits{' '}
            <strong style={{ color: 'var(--color-success-text)' }}>
              {fmt(totals.creditTotal)}
            </strong>
          </span>
          <span>
            Net effect on balance{' '}
            <strong style={{ color: 'var(--text)' }}>
              {totals.net.isZero()
                ? fmt(totals.net)
                : totals.net.isPositive()
                  ? `+${fmt(totals.net)}`
                  : `−${fmt(phpFromCents(Math.abs(totals.net.getAmount())))}`}
            </strong>
          </span>
        </div>
      )}

      {/* Table */}
      {rows.length === 0 ? (
        <div
          style={{
            border: '1px dashed var(--border)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--sp-4)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
            fontStyle: 'italic',
            textAlign: 'center',
          }}
        >
          No adjustments recorded for this invoice.
        </div>
      ) : (
        <div
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-md)',
            overflow: 'hidden',
            background: 'var(--bg-surface)',
          }}
        >
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}
          >
            <thead>
              <tr style={{ background: 'var(--bg-muted, #f9fafb)' }}>
                <Th>Date</Th>
                <Th>Type</Th>
                <Th align="right">Amount</Th>
                <Th>Reason</Th>
                <Th>Note</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const isDebit = row.adjustment_type === 'debit';
                return (
                  <tr key={row.id} style={{ borderTop: '1px solid var(--border)' }}>
                    <Td>{fmtDate(row.adjusted_at ?? row.created_at)}</Td>
                    <Td>
                      <TypePill type={isDebit ? 'debit' : 'credit'} />
                    </Td>
                    <Td
                      align="right"
                      mono
                      color={isDebit ? 'var(--color-warning-text)' : 'var(--color-success-text)'}
                    >
                      {isDebit ? '+' : '−'}
                      {fmt(phpFromCents(row.amount_cents ?? 0))}
                    </Td>
                    <Td>{row.reason ?? '—'}</Td>
                    <Td muted>{row.note ?? '—'}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Cell helpers ───

function Th({ children, align }: { children: React.ReactNode; align?: 'right' }) {
  return (
    <th
      style={{
        textAlign: align ?? 'left',
        padding: 'var(--sp-2) var(--sp-3)',
        fontWeight: 600,
        color: 'var(--text-muted)',
        fontSize: 'var(--font-size-xs)',
        textTransform: 'uppercase',
        letterSpacing: 0.4,
      }}
    >
      {children}
    </th>
  );
}

function Td({
  children,
  align,
  mono,
  muted,
  color,
}: {
  children: React.ReactNode;
  align?: 'right';
  mono?: boolean;
  muted?: boolean;
  color?: string;
}) {
  return (
    <td
      style={{
        textAlign: align ?? 'left',
        padding: 'var(--sp-2) var(--sp-3)',
        color: color ?? (muted ? 'var(--text-muted)' : 'var(--text)'),
        fontVariantNumeric: mono ? 'tabular-nums' : undefined,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </td>
  );
}

function TypePill({ type }: { type: 'debit' | 'credit' }) {
  const isDebit = type === 'debit';
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '1px 8px',
        borderRadius: 'var(--radius-full)',
        background: isDebit ? 'var(--color-warning-bg)' : 'var(--color-success-bg)',
        color: isDebit ? 'var(--color-warning-text)' : 'var(--color-success-text)',
        fontSize: 'var(--font-size-xs)',
        fontWeight: 500,
        textTransform: 'capitalize',
      }}
    >
      {type}
    </span>
  );
}
